import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar as CalIcon,
  Clock,
  MapPin,
  User,
  Building2,
  FileText,
  MessageSquare,
  Send,
  Navigation,
  ExternalLink,
  Play,
  Truck,
  RefreshCw,
  CheckCircle2,
  Camera,
  X as XIcon,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import type { Job } from '../lib/store';
import StatusBadge from '../components/StatusBadge';
import { statusColor, isJobMine } from '../lib/utils';
import { useAuth } from '../lib/auth';
import PushToXeroModal from '../components/PushToXeroModal';
import PartsOrdersSection from '../components/PartsOrdersSection';
import MaterialsUsedSection from '../components/MaterialsUsedSection';
import FollowUpModal from '../components/FollowUpModal';
import ConfirmHoursModal from '../components/ConfirmHoursModal';
import BoilerServiceReportModal from '../components/BoilerServiceReportModal';
import BoilerReportCard from '../components/BoilerReportCard';

const STATUSES = [
  'Pending',
  'In Progress',
  'Awaiting Parts',
  'Follow-up Visit Required',
  'Ready to Invoice',
  'Completed',
  'Invoiced',
  'Cancelled',
];

export default function JobDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const [job, setJob] = useState<Job | null>(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [commentPhoto, setCommentPhoto] = useState<string | null>(null);
  const [posting, setPosting] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [xeroModalOpen, setXeroModalOpen] = useState(false);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [hoursOpen, setHoursOpen] = useState<null | 'Ready to Invoice' | 'Awaiting Parts' | 'Follow-up Visit Required'>(null);
  const [boilerReportOpen, setBoilerReportOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const isBoilerJob = (job?.type || '').toLowerCase().includes('boiler');
  // Support both legacy single service_report and new service_reports array
  const serviceReports: any[] = (() => {
    const j = job as any;
    if (!j) return [];
    if (Array.isArray(j.service_reports)) return j.service_reports;
    if (j.service_report && j.service_report.kind) return [j.service_report];
    return [];
  })();
  const [editingReportId, setEditingReportId] = useState<string | null>(null);
  const hasBoilerReport = serviceReports.some((r) => r.kind === 'boiler_service');

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await api.get(`/jobs/${id}`);
      setJob(res.data);
    } catch (e) {
      toast.error('Failed to load job');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const changeStatus = async (status: string) => {
    if (!job || status === job.status) return;
    setSavingStatus(true);
    try {
      const res = await api.put(`/jobs/${job.id}`, { status });
      setJob(res.data);
      toast.success(`Status updated to “${status}”`);
    } catch (e) {
      toast.error('Failed to update status');
    } finally {
      setSavingStatus(false);
    }
  };

  const addComment = async () => {
    if ((!comment.trim() && !commentPhoto) || !job) return;
    setPosting(true);
    try {
      const res = await api.post(`/jobs/${job.id}/comments`, {
        message: comment.trim() || (commentPhoto ? '📷 Photo' : ''),
        photo: commentPhoto,
      });
      setJob(res.data);
      setComment('');
      setCommentPhoto(null);
      toast.success('Comment added');
    } catch (e) {
      toast.error('Failed to add comment');
    } finally {
      setPosting(false);
    }
  };

  const handlePhotoPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setCommentPhoto(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  if (loading) {
    return (
      <div className="card p-12 text-center">
        <div className="animate-spin h-8 w-8 rounded-full border-4 border-brand-600 border-t-transparent mx-auto" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="card p-8 text-center">
        <p className="text-slate-600">Job not found.</p>
        <button onClick={() => navigate('/jobs')} className="btn btn-secondary mt-4">
          Back to Jobs
        </button>
      </div>
    );
  }

  const address = job.location?.address || job.client_address;

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/jobs"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-3"
        >
          <ArrowLeft className="h-4 w-4" /> Back to jobs
        </Link>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {job.job_number && (
                <span className="text-xs font-bold text-slate-400 tracking-wider">
                  {job.job_number}
                </span>
              )}
              <StatusBadge status={job.status} />
              <span className="text-xs font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
                {job.type}
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{job.title}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
          {address && (
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                address
              )}`}
              target="_blank"
              rel="noreferrer"
              className="btn btn-secondary"
            >
              <Navigation className="h-4 w-4" /> Directions
            </a>
          )}
          {isAdmin && job.status === 'In Progress' && (
            <button onClick={() => setFollowUpOpen(true)} className="btn btn-secondary">
              <RefreshCw className="h-4 w-4" /> Book follow-up
            </button>
          )}
          {isAdmin && job.status === 'Ready to Invoice' && (
            <button onClick={() => setXeroModalOpen(true)} className="btn btn-primary">
              <Send className="h-4 w-4" /> Push to Xero
            </button>
          )}
          {isAdmin && job.status === 'Invoiced' && job.invoice_number && (
            <a
              href={`https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${(job as any).xero_invoice_id || ''}`}
              target="_blank"
              rel="noreferrer"
              className="btn btn-secondary"
            >
              <ExternalLink className="h-4 w-4" /> View in Xero
            </a>
          )}
          </div>
        </div>
      </div>

      {/* Worker action bar — shown when this job is assigned to current user */}
      {!isAdmin && isJobMine(job as any, user) && (
        <WorkerActionBar
          job={job}
          onChangeStatus={changeStatus}
          onAwaitingParts={() => setHoursOpen('Awaiting Parts')}
          onFollowUp={() => setHoursOpen('Follow-up Visit Required')}
          onComplete={() => setHoursOpen('Ready to Invoice')}
          savingStatus={savingStatus}
        />
      )}

      {/* Boiler certificate prompt (any user, when job is a boiler service and no report yet) */}
      {isBoilerJob && !hasBoilerReport && (
        <button
          onClick={() => setBoilerReportOpen(true)}
          className="w-full rounded-2xl border-2 border-dashed border-red-300 bg-red-50/40 hover:bg-red-50 hover:border-red-400 p-4 flex items-center gap-3 transition text-left"
        >
          <div className="w-10 h-10 rounded-xl bg-red-100 grid place-items-center shrink-0">
            <svg className="h-5 w-5 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/></svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900">Create Boiler Service Certificate</div>
            <div className="text-xs text-slate-500">
              Gas Safe checklist · combustion readings · sign-off · attached to invoice
            </div>
          </div>
          <span className="text-xs font-bold text-red-700 shrink-0">Start →</span>
        </button>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Main */}
        <div className="lg:col-span-2 space-y-4">
          {/* Info card */}
          <div className="card p-5 sm:p-6">
            <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4">
              Details
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <InfoRow icon={Building2} label="Client" value={job.client_name || '—'} />
              <InfoRow icon={User} label="Assigned to" value={job.assigned_to || '—'} />
              <InfoRow
                icon={CalIcon}
                label="Date"
                value={job.date ? format(parseISO(job.date), 'EEEE d MMMM yyyy') : '—'}
              />
              <InfoRow
                icon={Clock}
                label="Time / Duration"
                value={
                  (job.time ? job.time + ' · ' : '') +
                  (job.duration_type === 'hours'
                    ? `${job.duration_hours ?? 0}h`
                    : `${job.duration_days ?? 1} day${(job.duration_days ?? 1) > 1 ? 's' : ''}`)
                }
              />
              {address && (
                <InfoRow icon={MapPin} label="Address" value={address} className="sm:col-span-2" />
              )}
              {job.notes && (
                <InfoRow
                  icon={FileText}
                  label="Notes"
                  value={job.notes}
                  className="sm:col-span-2"
                />
              )}
            </div>
          </div>

          {/* Boiler service reports (may be multiple) */}
          {serviceReports.map((report, idx) => (
            <BoilerReportCard
              key={report.id || idx}
              jobId={job.id}
              hasXeroInvoice={!!(job as any).xero_invoice_id}
              report={report}
              onEdit={() => {
                setEditingReportId(report.id || `idx-${idx}`);
                setBoilerReportOpen(true);
              }}
            />
          ))}
          {isBoilerJob && hasBoilerReport && (
            <button
              onClick={() => {
                setEditingReportId(null);
                setBoilerReportOpen(true);
              }}
              className="w-full rounded-xl border-2 border-dashed border-slate-300 hover:border-red-400 hover:bg-red-50/40 p-3 text-sm font-semibold text-slate-600 hover:text-red-700 transition"
            >
              + Add another boiler certificate
            </button>
          )}

          {/* Materials used (editable) */}
          <MaterialsUsedSection job={job} onSaved={load} />

          {/* Parts orders */}
          <PartsOrdersSection jobId={job.id} onJobRefresh={load} />

          {/* Comments */}
          <div className="card p-5 sm:p-6">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className="h-4 w-4 text-slate-500" />
              <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider">
                Comments · {job.comments?.length || 0}
              </h3>
            </div>

            {(!job.comments || job.comments.length === 0) && (
              <p className="text-sm text-slate-500 mb-4">No comments yet. Be the first to add one.</p>
            )}

            {job.comments && job.comments.length > 0 && (
              <div className="space-y-3 mb-4">
                {job.comments.map((c: any) => (
                  <div
                    key={c.id}
                    className="rounded-lg bg-slate-50 border border-slate-100 p-3"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-bold text-slate-700">{c.user}</span>
                      <span className="text-xs text-slate-400">
                        {c.timestamp ? format(new Date(c.timestamp), 'd MMM yyyy, HH:mm') : ''}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.message}</p>
                    {c.photo && (
                      <img
                        src={c.photo}
                        alt="Comment attachment"
                        className="mt-2 rounded-lg max-h-64 border border-slate-200"
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {commentPhoto && (
              <div className="mb-3 relative inline-block">
                <img
                  src={commentPhoto}
                  alt="Preview"
                  className="max-h-48 rounded-lg border border-slate-200"
                />
                <button
                  onClick={() => setCommentPhoto(null)}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-slate-900/70 text-white grid place-items-center hover:bg-slate-900"
                >
                  <XIcon className="h-3 w-3" />
                </button>
              </div>
            )}

            <div className="flex gap-2">
              <input
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addComment()}
                placeholder="Add a comment…"
                className="input"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoPick}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="btn btn-secondary"
                title="Attach photo"
              >
                <Camera className="h-4 w-4" />
              </button>
              <button
                onClick={addComment}
                disabled={(!comment.trim() && !commentPhoto) || posting}
                className="btn btn-primary"
              >
                <Send className="h-4 w-4" />
                <span className="hidden sm:inline">Post</span>
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="card p-5">
            <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">
              Change status
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {STATUSES.map((s) => {
                const active = job.status === s;
                const c = statusColor(s);
                return (
                  <button
                    key={s}
                    onClick={() => changeStatus(s)}
                    disabled={savingStatus || active}
                    className={`text-xs font-semibold rounded-lg px-3 py-2 border transition text-left ${
                      active
                        ? `${c.bg} ${c.text} border-transparent ring-2 ${c.ring}`
                        : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          {job.invoice_number && (
            <div className="card p-5">
              <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">
                Invoice
              </h3>
              <div className="text-lg font-bold text-slate-900">{job.invoice_number}</div>
              {job.hours_to_invoice != null && (
                <div className="text-sm text-slate-500 mt-0.5">
                  {job.hours_to_invoice} hour{job.hours_to_invoice === 1 ? '' : 's'} billed
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {xeroModalOpen && (
        <PushToXeroModal
          job={job}
          onClose={() => setXeroModalOpen(false)}
          onPushed={() => load()}
        />
      )}
      {followUpOpen && (
        <FollowUpModal
          parent={job}
          onClose={() => setFollowUpOpen(false)}
          onBooked={() => load()}
        />
      )}
      {hoursOpen && (
        <ConfirmHoursModal
          job={job}
          nextStatus={hoursOpen}
          onClose={() => setHoursOpen(null)}
          onConfirmed={({ openFollowUp }) => {
            load();
            if (openFollowUp) setFollowUpOpen(true);
            // Auto-prompt for boiler certificate on finish if it's a boiler job and no report yet
            if (hoursOpen === 'Ready to Invoice' && isBoilerJob && !hasBoilerReport) {
              setTimeout(() => setBoilerReportOpen(true), 400);
            }
          }}
        />
      )}
      {boilerReportOpen && (
        <BoilerServiceReportModal
          job={job}
          editingReport={
            editingReportId
              ? serviceReports.find(
                  (r, i) => (r.id || `idx-${i}`) === editingReportId
                ) || null
              : null
          }
          onClose={() => {
            setBoilerReportOpen(false);
            setEditingReportId(null);
          }}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}

function InfoRow({
  icon: Icon,
  label,
  value,
  className = '',
}: {
  icon: any;
  label: string;
  value: string;
  className?: string;
}) {
  return (
    <div className={`flex gap-3 items-start ${className}`}>
      <div className="w-9 h-9 rounded-lg bg-slate-100 grid place-items-center shrink-0">
        <Icon className="h-4 w-4 text-slate-500" />
      </div>
      <div className="min-w-0">
        <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">{label}</div>
        <div className="text-sm text-slate-900 font-medium mt-0.5 break-words">{value}</div>
      </div>
    </div>
  );
}

function WorkerActionBar({
  job,
  onChangeStatus,
  onAwaitingParts,
  onFollowUp,
  onComplete,
  savingStatus,
}: {
  job: Job;
  onChangeStatus: (s: string) => void;
  onAwaitingParts: () => void;
  onFollowUp: () => void;
  onComplete: () => void;
  savingStatus: boolean;
}) {
  const isInProgress = job.status === 'In Progress';

  // Colour palette
  const c = statusColor(job.status);

  return (
    <div className={`rounded-2xl border-2 ${c.ring} bg-white shadow-card p-5`}>
      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
        What's next
      </div>
      {!isInProgress && job.status !== 'Completed' && job.status !== 'Invoiced' ? (
        <button
          onClick={() => onChangeStatus('In Progress')}
          disabled={savingStatus}
          className="btn btn-primary btn-lg w-full"
        >
          <Play className="h-5 w-5" /> Start this job
        </button>
      ) : isInProgress ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <button
            onClick={onAwaitingParts}
            disabled={savingStatus}
            className="btn btn-secondary py-3 flex-col sm:flex-row"
          >
            <Truck className="h-4 w-4 text-orange-600" />
            <span>Awaiting parts</span>
          </button>
          <button
            onClick={onFollowUp}
            disabled={savingStatus}
            className="btn btn-secondary py-3 flex-col sm:flex-row"
          >
            <RefreshCw className="h-4 w-4 text-yellow-600" />
            <span>Follow-up needed</span>
          </button>
          <button
            onClick={onComplete}
            disabled={savingStatus}
            className="btn py-3 flex-col sm:flex-row bg-emerald-600 text-white hover:bg-emerald-700"
          >
            <CheckCircle2 className="h-4 w-4" />
            <span>Mark complete</span>
          </button>
        </div>
      ) : (
        <p className="text-sm text-slate-600 text-center py-2">
          Job is <strong>{job.status}</strong>. Nothing to do for now.
        </p>
      )}
    </div>
  );
}
